import { useState, useRef } from 'react';
import { useCreditsContext } from '@/context/CreditsContext';
import { EDIT_MODES, getEditModeConfig, type EditModeId } from '@/config/imageEngines';
import { getEdgeFunctionHeaders } from '@/lib/edgeFunctions';
import { getSupabaseFunctionUrl } from '@/lib/supabase';

const EDIT_AD_IMAGE_URL = getSupabaseFunctionUrl('edit-ad-image');

const PRESET_CHIPS: { category: string; color: string; chips: { label: string; icon: string }[] }[] = [
  {
    category: 'Lighting',
    color: 'amber',
    chips: [
      { label: 'Warmer lighting', icon: 'ri-sun-line' },
      { label: 'Golden hour glow', icon: 'ri-sun-foggy-line' },
      { label: 'Softer, diffused light', icon: 'ri-contrast-drop-line' },
      { label: 'More dramatic shadows', icon: 'ri-moon-line' },
    ],
  },
  {
    category: 'Atmosphere',
    color: 'sky',
    chips: [
      { label: 'More dramatic sky', icon: 'ri-cloud-windy-line' },
      { label: 'Misty atmosphere', icon: 'ri-mist-line' },
      { label: 'Bright & airy feel', icon: 'ri-sun-line' },
      { label: 'Darker, moodier tone', icon: 'ri-thunderstorms-line' },
    ],
  },
  {
    category: 'Composition',
    color: 'stone',
    chips: [
      { label: 'Remove background clutter', icon: 'ri-scissors-cut-line' },
      { label: 'Clean minimal background', icon: 'ri-artboard-line' },
      { label: 'More negative space', icon: 'ri-expand-width-line' },
      { label: 'Tighter crop on subject', icon: 'ri-crop-line' },
    ],
  },
  {
    category: 'Color',
    color: 'teal',
    chips: [
      { label: 'More vibrant colors', icon: 'ri-palette-line' },
      { label: 'Cinematic color grade', icon: 'ri-film-line' },
      { label: 'Rich, saturated tones', icon: 'ri-contrast-fill' },
      { label: 'Muted & desaturated', icon: 'ri-contrast-drop-2-line' },
    ],
  },
];

const CHIP_STYLES: Record<string, { bg: string; text: string; border: string; hoverBg: string }> = {
  amber: { bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200', hoverBg: 'hover:bg-amber-100' },
  sky:   { bg: 'bg-sky-50',   text: 'text-sky-700',   border: 'border-sky-200',   hoverBg: 'hover:bg-sky-100'   },
  stone: { bg: 'bg-stone-100', text: 'text-stone-600', border: 'border-stone-200', hoverBg: 'hover:bg-stone-200' },
  teal:  { bg: 'bg-teal-50',  text: 'text-teal-700',  border: 'border-teal-200',  hoverBg: 'hover:bg-teal-100'  },
};

export interface EditHistoryEntry {
  prompt: string;
  url: string;
}

interface EditImagePanelProps {
  /** The current image URL to edit */
  imageUrl: string;
  /** Stable key for per-image free-edit tracking (usually the original generated URL) */
  imageKey?: string;
  /** Called when a new edited image URL is ready */
  onImageEdited: (newUrl: string) => void;
  /** Edit history for this image */
  editHistory: EditHistoryEntry[];
  /** Called when history is updated */
  onHistoryUpdate: (entries: EditHistoryEntry[]) => void;
  /** Called when user wants to revert to original */
  onRevertToOriginal?: () => void;
  /** Whether the image has been edited from original */
  hasEdit?: boolean;
  /** Creative index (for credit tracking) */
  creativeIndex?: number;
}

export default function EditImagePanel({
  imageUrl,
  imageKey,
  onImageEdited,
  editHistory,
  onHistoryUpdate,
  onRevertToOriginal,
  hasEdit = false,
  creativeIndex = 0,
}: EditImagePanelProps) {
  const { creditsRemaining, hasEnoughCredits, refetch: refetchCredits } = useCreditsContext();

  const [isOpen, setIsOpen] = useState(false);
  const [editPrompt, setEditPrompt] = useState('');
  const [editMode, setEditMode] = useState<EditModeId>('basic');
  const [isApplying, setIsApplying] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);
  const [fallbackMsg, setFallbackMsg] = useState<string | null>(null);
  const [showAllChips, setShowAllChips] = useState(false);

  // Reference image state
  const [refImageBase64, setRefImageBase64] = useState<string | null>(null);
  const [refImagePreview, setRefImagePreview] = useState<string | null>(null);
  const [refImagePrompt, setRefImagePrompt] = useState('');
  const refFileInputRef = useRef<HTMLInputElement>(null);

  const handleRefImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const dataUrl = ev.target?.result as string;
      setRefImagePreview(dataUrl);
      // Strip the data:...;base64, prefix to get raw base64
      const base64 = dataUrl.split(',')[1] ?? null;
      setRefImageBase64(base64);
    };
    reader.readAsDataURL(file);
    // Reset input so same file can be re-selected
    e.target.value = '';
  };

  const handleRemoveRefImage = () => {
    setRefImageBase64(null);
    setRefImagePreview(null);
    setRefImagePrompt('');
  };

  const handleApplyEdit = async () => {
    if (!editPrompt.trim() || isApplying) return;
    const modeConfig = getEditModeConfig(editMode);
    if (!hasEnoughCredits(modeConfig.credits)) {
      setEditError(`Not enough credits. This edit requires ${modeConfig.credits} credits.`);
      return;
    }

    setIsApplying(true);
    setEditError(null);

    try {
      const adKey = imageKey ?? imageUrl;
      const response = await fetch(EDIT_AD_IMAGE_URL, {
        method: 'POST',
        headers: await getEdgeFunctionHeaders(),
        body: JSON.stringify({
          imageUrl,
          editPrompt: editPrompt.trim(),
          creativeIndex,
          imageKey: adKey,
          editMode,
          provider: modeConfig.provider,
          referenceImageBase64: refImageBase64 ?? undefined,
          referenceImagePrompt: refImageBase64 && refImagePrompt.trim() ? refImagePrompt.trim() : undefined,
        }),
      });

      const data = await response.json() as Record<string, unknown>;

      if (!response.ok) {
        const msg = typeof data?.error === 'string' ? data.error : `Edit failed (${response.status})`;
        throw new Error(msg);
      }
      if (data?.error) throw new Error(String(data.error));
      if (!data?.imageUrl) throw new Error('No image returned');

      const newUrl = data.imageUrl as string;
      onImageEdited(newUrl);
      onHistoryUpdate([...editHistory, { prompt: editPrompt.trim(), url: newUrl }]);
      setEditPrompt('');
      await refetchCredits();

      if (data.fallbackUsed && data.fallbackMessage) {
        setFallbackMsg(data.fallbackMessage as string);
        setTimeout(() => setFallbackMsg(null), 6000);
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : '';
      setEditError(
        message === 'Failed to fetch'
          ? 'Could not reach the image editor. Please try again in a moment.'
          : message || 'Something went wrong. Please try again.'
      );
    } finally {
      setIsApplying(false);
    }
  };

  const handleRevertToVersion = (url: string) => {
    onImageEdited(url);
  };

  return (
    <div className="rounded-xl border border-stone-200 overflow-hidden">
      {/* Toggle header */}
      <button
        onClick={() => { setIsOpen((o) => !o); setEditError(null); }}
        className={`w-full flex items-center gap-3 px-4 py-3 transition-colors cursor-pointer ${
          isOpen ? 'bg-orange-50 border-b border-orange-100' : 'bg-stone-50 hover:bg-stone-100'
        }`}
      >
        <div className={`w-7 h-7 flex items-center justify-center rounded-lg border shrink-0 transition-colors ${
          isOpen ? 'bg-orange-500 border-orange-500 text-white' : 'bg-white border-stone-200 text-stone-500'
        }`}>
          <i className="ri-magic-line text-sm" />
        </div>
        <div className="flex-1 text-left">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs font-bold text-stone-800">Edit this image</span>
            {hasEdit && editHistory.length > 0 && (
              <span className="text-xs text-orange-600 font-semibold bg-orange-50 border border-orange-100 px-1.5 py-0.5 rounded-full">
                {editHistory.length} edit{editHistory.length !== 1 ? 's' : ''} applied
              </span>
            )}
          </div>
          <p className="text-xs text-stone-400 mt-0.5">Describe changes and the AI will apply them to this image</p>
        </div>
        <i className={`${isOpen ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'} text-stone-400 text-lg shrink-0`} />
      </button>

      {/* Panel body */}
      {isOpen && (
        <div className="px-4 py-4 bg-white space-y-4">
          {/* Quick suggestion chips */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <p className="text-xs font-semibold text-stone-500 uppercase tracking-wider">Quick suggestions</p>
              <button
                onClick={() => setShowAllChips((p) => !p)}
                className="text-xs text-stone-400 hover:text-orange-500 transition-colors cursor-pointer flex items-center gap-1"
              >
                {showAllChips ? (
                  <><i className="ri-arrow-up-s-line text-sm" /> Less</>
                ) : (
                  <><i className="ri-arrow-down-s-line text-sm" /> More</>
                )}
              </button>
            </div>
            <div className="space-y-2">
              {PRESET_CHIPS.slice(0, showAllChips ? PRESET_CHIPS.length : 2).map((group) => {
                const style = CHIP_STYLES[group.color] ?? CHIP_STYLES.stone;
                return (
                  <div key={group.category} className="space-y-1">
                    <p className="text-xs text-stone-400 font-medium">{group.category}</p>
                    <div className="flex flex-wrap gap-1.5">
                      {group.chips.map((chip) => (
                        <button
                          key={chip.label}
                          onClick={() => {
                            setEditPrompt((prev) =>
                              prev.trim() ? `${prev.trim()}, ${chip.label.toLowerCase()}` : chip.label
                            );
                          }}
                          className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-full border text-xs font-medium transition-all duration-150 cursor-pointer whitespace-nowrap ${style.bg} ${style.text} ${style.border} ${style.hoverBg}`}
                        >
                          <i className={`${chip.icon} text-xs`} />
                          {chip.label}
                        </button>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="h-px bg-stone-100" />

          {/* Prompt input */}
          <div>
            <label className="block text-xs font-semibold text-stone-600 uppercase tracking-wider mb-2">
              Describe your edit
            </label>
            <textarea
              value={editPrompt}
              onChange={(e) => setEditPrompt(e.target.value)}
              placeholder="e.g. Change the lighting to golden hour sunset, add a dramatic sky, make the background more vibrant, change the color scheme to warm tones..."
              rows={3}
              maxLength={500}
              className="w-full text-sm px-4 py-3 rounded-xl border border-stone-200 bg-stone-50 text-stone-800 placeholder:text-stone-400 focus:outline-none focus:border-orange-400 focus:bg-white transition-all duration-200 resize-none"
            />
            <div className="flex items-center justify-between mt-1">
              <p className="text-xs text-stone-400">Be specific — mention colors, lighting, composition, mood, etc.</p>
              <span className="text-xs text-stone-400">{editPrompt.length}/500</span>
            </div>
          </div>

          {/* ── Reference Image Upload ── */}
          <div>
            <div className="flex items-center gap-2 mb-2">
              <label className="block text-xs font-semibold text-stone-600 uppercase tracking-wider">
                Reference Image
              </label>
              <span className="text-xs text-stone-400 font-medium bg-stone-100 px-1.5 py-0.5 rounded-full">Optional</span>
            </div>
            <p className="text-xs text-stone-400 mb-3 leading-relaxed">
              Upload a product, logo, or scene you want the AI to incorporate or recreate in the edited image.
            </p>

            {!refImagePreview ? (
              <button
                type="button"
                onClick={() => refFileInputRef.current?.click()}
                className="w-full flex flex-col items-center justify-center gap-2 px-4 py-5 rounded-xl border-2 border-dashed border-stone-200 bg-stone-50 hover:border-orange-300 hover:bg-orange-50 transition-all duration-200 cursor-pointer group"
              >
                <div className="w-9 h-9 flex items-center justify-center rounded-xl bg-stone-200 group-hover:bg-orange-100 transition-colors">
                  <i className="ri-image-add-line text-stone-500 group-hover:text-orange-500 text-lg transition-colors" />
                </div>
                <div className="text-center">
                  <p className="text-xs font-semibold text-stone-600 group-hover:text-orange-600 transition-colors">Upload reference image</p>
                  <p className="text-xs text-stone-400 mt-0.5">PNG, JPG, WEBP · Click to browse</p>
                </div>
              </button>
            ) : (
              <div className="space-y-3">
                {/* Preview */}
                <div className="relative rounded-xl overflow-hidden border border-stone-200 bg-stone-100">
                  <img
                    src={refImagePreview}
                    alt="Reference"
                    className="w-full max-h-40 object-contain bg-stone-50"
                  />
                  <div className="absolute top-2 left-2 flex items-center gap-1.5 bg-orange-500 text-white text-xs font-bold px-2 py-1 rounded-full">
                    <i className="ri-image-line text-xs" />
                    Reference uploaded
                  </div>
                  <div className="absolute top-2 right-2 flex items-center gap-1.5">
                    <button
                      type="button"
                      onClick={() => refFileInputRef.current?.click()}
                      className="flex items-center gap-1 bg-white/90 hover:bg-white text-stone-600 text-xs font-semibold px-2 py-1 rounded-lg border border-stone-200 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      <i className="ri-refresh-line text-xs" />
                      Replace
                    </button>
                    <button
                      type="button"
                      onClick={handleRemoveRefImage}
                      className="w-6 h-6 flex items-center justify-center bg-white/90 hover:bg-red-50 text-stone-500 hover:text-red-500 rounded-lg border border-stone-200 transition-colors cursor-pointer"
                    >
                      <i className="ri-close-line text-xs" />
                    </button>
                  </div>
                </div>

                {/* How to use prompt */}
                <div>
                  <label className="block text-xs font-semibold text-stone-600 mb-1.5">
                    How should AI use this image?
                  </label>
                  <input
                    type="text"
                    value={refImagePrompt}
                    onChange={(e) => setRefImagePrompt(e.target.value)}
                    placeholder='e.g. "Recreate this exact can of beer as the hero of the image, keep the label visible"'
                    maxLength={300}
                    className="w-full text-sm px-3 py-2.5 rounded-xl border border-stone-200 bg-stone-50 text-stone-800 placeholder:text-stone-400 focus:outline-none focus:border-orange-400 focus:bg-white transition-all duration-200"
                  />
                  <p className="text-xs text-stone-400 mt-1">Tell the AI exactly how to incorporate your reference into the scene.</p>
                </div>
              </div>
            )}

            <input
              ref={refFileInputRef}
              type="file"
              accept="image/png,image/jpeg,image/webp"
              className="hidden"
              onChange={handleRefImageUpload}
            />
          </div>

          {/* Error */}
          {editError && (
            <div className="flex items-start gap-2.5 bg-red-50 border border-red-200 text-red-700 text-xs px-3 py-2.5 rounded-lg">
              <i className="ri-error-warning-line text-sm shrink-0 mt-0.5" />
              <span className="leading-snug flex-1">{editError}</span>
              <button onClick={() => setEditError(null)} className="ml-auto shrink-0 text-red-400 hover:text-red-600 cursor-pointer">
                <i className="ri-close-line" />
              </button>
            </div>
          )}

          {/* Fallback notice */}
          {fallbackMsg && (
            <div className="flex items-center gap-2.5 bg-amber-50 border border-amber-200 text-amber-700 text-xs px-3 py-2.5 rounded-lg">
              <i className="ri-information-line text-sm shrink-0 text-amber-500" />
              <span className="flex-1 leading-snug">{fallbackMsg}</span>
              <button onClick={() => setFallbackMsg(null)} className="shrink-0 text-amber-400 hover:text-amber-600 cursor-pointer">
                <i className="ri-close-line" />
              </button>
            </div>
          )}

          {/* Edit quality selector */}
          <div>
            <label className="block text-xs font-semibold text-stone-600 uppercase tracking-wider mb-2">
              Edit Quality
            </label>
            <div className="flex flex-col gap-1.5">
              {EDIT_MODES.map((mode) => {
                const isSelected = editMode === mode.id;
                const affordable = hasEnoughCredits(mode.credits);
                return (
                  <button
                    key={mode.id}
                    onClick={() => setEditMode(mode.id)}
                    className={`relative flex items-center gap-3 px-3 py-2.5 rounded-xl border-2 text-left transition-all duration-200 cursor-pointer ${
                      isSelected
                        ? 'border-orange-400 bg-orange-50'
                        : 'border-stone-200 bg-stone-50 hover:border-stone-300 hover:bg-white'
                    }`}
                  >
                    <div className={`shrink-0 w-3.5 h-3.5 rounded-full border-2 flex items-center justify-center ${
                      isSelected ? 'border-orange-500 bg-orange-500' : 'border-stone-300'
                    }`}>
                      {isSelected && <div className="w-1 h-1 rounded-full bg-white" />}
                    </div>
                    <div className={`shrink-0 w-7 h-7 flex items-center justify-center rounded-lg transition-colors ${
                      isSelected ? 'bg-orange-500 text-white' : 'bg-stone-200 text-stone-500'
                    }`}>
                      <i className={`${mode.icon} text-xs`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <span className={`text-xs font-bold ${isSelected ? 'text-orange-800' : 'text-stone-700'}`}>
                        {mode.label}
                      </span>
                      <p className={`text-xs leading-tight mt-0.5 ${isSelected ? 'text-orange-500' : 'text-stone-400'}`}>
                        {mode.description}
                      </p>
                    </div>
                    <div className="shrink-0 text-right">
                      <span className={`text-sm font-extrabold ${isSelected ? 'text-orange-600' : affordable ? 'text-stone-700' : 'text-red-400'}`}>
                        {mode.credits}
                      </span>
                      <span className={`text-xs ml-0.5 ${isSelected ? 'text-orange-400' : 'text-stone-400'}`}>cr</span>
                    </div>
                  </button>
                );
              })}
            </div>
            <div className="flex items-center justify-between mt-1.5 px-0.5">
              <p className="text-xs text-stone-400">{getEditModeConfig(editMode).recommendedUse}</p>
              <span className={`text-xs font-semibold ${hasEnoughCredits(getEditModeConfig(editMode).credits) ? 'text-stone-500' : 'text-red-500'}`}>
                Balance: {creditsRemaining} cr
              </span>
            </div>
          </div>

          {/* Apply button */}
          <button
            onClick={handleApplyEdit}
            disabled={!editPrompt.trim() || isApplying || !hasEnoughCredits(getEditModeConfig(editMode).credits)}
            className={`w-full flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-bold transition-all duration-200 whitespace-nowrap ${
              editPrompt.trim() && !isApplying && hasEnoughCredits(getEditModeConfig(editMode).credits)
                ? 'bg-gradient-to-r from-orange-500 to-amber-400 text-white hover:from-orange-600 hover:to-amber-500 cursor-pointer'
                : 'bg-stone-100 text-stone-400 cursor-not-allowed'
            }`}
          >
            {isApplying ? (
              <><i className="ri-loader-4-line animate-spin" /> Applying edit — ~20 seconds...</>
            ) : (
              <><i className="ri-magic-line" /> Apply {getEditModeConfig(editMode).label} — {getEditModeConfig(editMode).credits} Credits</>
            )}
          </button>

          {/* Edit history */}
          {editHistory.length > 0 && (
            <div className="space-y-2 pt-1 border-t border-stone-100">
              <div className="flex items-center justify-between">
                <p className="text-xs font-bold text-stone-500 uppercase tracking-widest">Edit History</p>
                {onRevertToOriginal && (
                  <button
                    onClick={onRevertToOriginal}
                    className="text-xs text-stone-400 hover:text-orange-500 transition-colors cursor-pointer flex items-center gap-1"
                  >
                    <i className="ri-history-line text-xs" /> Revert to original
                  </button>
                )}
              </div>
              <div className="space-y-1.5">
                {editHistory.map((entry, i) => (
                  <div
                    key={i}
                    className="flex items-center gap-3 bg-stone-50 border border-stone-100 rounded-lg px-3 py-2"
                  >
                    <div className="w-8 h-8 rounded-lg overflow-hidden shrink-0 bg-stone-200">
                      <img src={entry.url} alt={`Edit ${i + 1}`} className="w-full h-full object-cover" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs text-stone-600 truncate italic">&ldquo;{entry.prompt}&rdquo;</p>
                      <p className="text-xs text-stone-400">Edit {i + 1}</p>
                    </div>
                    <button
                      onClick={() => handleRevertToVersion(entry.url)}
                      className="shrink-0 text-xs text-stone-400 hover:text-orange-500 transition-colors cursor-pointer px-2 py-1 rounded-lg hover:bg-orange-50 border border-transparent hover:border-orange-100 whitespace-nowrap"
                    >
                      Use this
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
